import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, FormArray } from '@angular/forms';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, debounceTime, first } from 'rxjs/operators';
import { UserService } from '../services/user.service';
import * as _ from 'lodash';

@Component({
    selector: 'current-user-invitation-manager',
    template: `
        <mat-card [formGroup]="form">
            <mat-card-title>{{title}}</mat-card-title>
            <mat-card-content>
                <div fxLayout="column">
                    <div>
                        <mat-form-field fxFlex.lt-lg="200px" fxFlex.gt-md="500px">
                            <input matInput [formControl]="searchText" placeholder="Search members">
                            <mat-icon matPrefix style="padding-right: 10px;">search</mat-icon>
                        </mat-form-field>
                    </div>
                    <div infiniteScroll [infiniteScrollThrottle]="300" [infiniteScrollDistance]="1" [scrollWindow]="false" (scrolled)="nextPage()" style="max-height: 400px; overflow-y: auto;">
                        <table mat-table fxFlex="100" *ngIf="currentUsers" #table [dataSource]="currentUsers" style="width: 100%">
                            <ng-container matColumnDef="name">
                                <th mat-header-cell *matHeaderCellDef>Name</th>
                                <td mat-cell *matCellDef="let row" >{{row.firstName}} {{row.lastName}}</td>
                            </ng-container>
                            <ng-container matColumnDef="email">
                                <th mat-header-cell *matHeaderCellDef>Email</th>
                                <td mat-cell *matCellDef="let row" >{{row.emailAddress}}</td>
                            </ng-container>
                            <ng-container matColumnDef="actions">
                                <th mat-header-cell *matHeaderCellDef width="100"></th>
                                <td mat-cell *matCellDef="let row; let i = index" [matTooltip]="selectionTooltip(row)">
                                    <button *ngIf="!isSelected(i)" 
                                            mat-raised-button 
                                            color="primary" 
                                            (click)="toggleSelection(i)" 
                                            style="margin-right: 45px;" 
                                            [disabled]="userIsCurrentlySubscribed(row)">Invite</button> 
                                    <button *ngIf="isSelected(i)" mat-raised-button matTooltip="Do not Invite" (click)="toggleSelection(i)">Do not Invite</button> 
                                </td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="['name', 'email', 'actions']"></tr>
                            <tr mat-row *matRowDef="let row; columns: ['name', 'email', 'actions'];"></tr>
                        </table>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>
    `
})
export class CurrentUserInvitationManagerComponent implements OnInit, OnDestroy {
    @Input() title: string;
    @Input() form: FormGroup;
    @Input() currentSubscribers; 
    searchText = new FormControl('');
    searchParams: { page, rows, searchTerm?, sort?, sortDirection?} = { page: 0, rows: 50, sort: 'lastName', sortDirection: 'ASC' };
    cleanupTime = new Subject<void>();
    currentUsers = new BehaviorSubject<any[]>([]);
    noTouching: boolean;
    isLast: any;

    constructor(private fb: FormBuilder, private userService: UserService) {
        this.searchText.valueChanges.pipe(
            takeUntil(this.cleanupTime),
            debounceTime(500)
        )
        .subscribe(text => {
            this.searchParams.page = 0;
            const invites = <FormArray>this.form.get('invitations');
            while (invites.length !== 0) {
                invites.removeAt(0)
            }
            this.isLast = false;
            this.currentUsers.next([]);
            this.searchParams.searchTerm = text;
            this.searchUsers();
        })
    }

    ngOnInit(): void {
        this.form.addControl('invitations', this.fb.array([]));
        this.searchUsers();
    }

    searchUsers() {
        this.userService.searchUsers(this.searchParams).pipe(first())
        .subscribe(results => {
            this.isLast = results.last;
            const invites = <FormArray>this.form.get('invitations');
            _.forEach(results.content, user => { 
                invites.push(this.fb.group({ 
                    emailAddress: user.emailAddress,
                    isClient: true, 
                    selected: false 
                })); 
            });
            this.currentUsers.next(this.currentUsers.value.concat(results.content));
            this.noTouching = false;
        });
    }

    selectionTooltip(user): string {
        return this.userIsCurrentlySubscribed(user) ? 'User is already subscribed' : 'Invite';
    }

    userIsCurrentlySubscribed(user): boolean {
        return !!_.find(this.currentSubscribers, subscriber => subscriber.id === user.id);
    }

    isSelected(index: number): boolean {
        return (<FormArray>this.form.get('invitations')).at(index).get('selected').value
    }

    toggleSelection(index: number): void {
        const ctrl = (<FormArray>this.form.get('invitations')).at(index).get('selected'); 
        ctrl.setValue(!ctrl.value);
    }

    nextPage(): void {
        if (!this.isLast) {
            this.searchParams.page++;
            this.searchUsers();   
        }
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}